import ProtectedComponent from './protected-component.js';
import logger from '../utils/logger.js';
import { renderSocietyOtter } from '../utils/mascots.js';

/**
 * @component group-create
 * @extends ProtectedComponent
 * @css group-create.css
 * @primitives card, btn-primary, btn-secondary
 *
 * /groups/new — create a new group.
 */
class GroupCreate extends ProtectedComponent {
  constructor() {
    super();
    this.error = null;
    this.submitting = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
    this.attachEventListeners();
  }

  setCurrentUser(user) {
    super.setCurrentUser(user);
    if (user) {
      this.render();
      this.attachEventListeners();
    } else {
      this.innerHTML = '';
    }
  }

  render() {
    if (!this.currentUser) {
      this.innerHTML = '';
      return;
    }

    const errorBlock = this.error
      ? `<div class="error-state" role="alert">${this.escapeHtml(this.error)}</div>`
      : '';

    this.innerHTML = `
      <div class="group-create-container">
        <div class="society-otter-helper">
          <div class="otter-avatar">
            ${renderSocietyOtter()}
          </div>
          <div class="otter-tip">
            <p>Let's gather your people! Pick a name and invite policy to get started. 🌱</p>
          </div>
        </div>

        <div class="group-create-header">
          <button type="button" class="btn-back" id="backBtn">← Back</button>
          <h1>Create Group</h1>
        </div>

        ${errorBlock}

        <form id="groupCreateForm" class="group-create-form card">
          <div class="form-section">
            <h2>Group Details</h2>

            <div class="form-group">
              <label class="form-label" for="groupName">Name *</label>
              <input
                type="text"
                id="groupName"
                name="name"
                class="form-control"
                maxlength="200"
                required
                placeholder="e.g., Weekly Game Night Crew"
              />
            </div>

            <div class="form-group">
              <label class="form-label" for="groupDescription">Description</label>
              <textarea
                id="groupDescription"
                name="description"
                class="form-control"
                rows="4"
                maxlength="2000"
                placeholder="What's this group about?"
              ></textarea>
            </div>

            <div class="form-group">
              <label class="form-label" for="invitePolicy">Invite policy</label>
              <select id="invitePolicy" name="invite_policy" class="form-control">
                <option value="members_allowed">Members can invite</option>
                <option value="mods_and_admins">Moderators / admins only</option>
                <option value="admins_only">Admins only</option>
              </select>
              <p class="form-text">Who can invite new members to this group.</p>
            </div>

            <div class="form-group">
              <label class="form-label" for="eventCreatePolicy">Event create policy</label>
              <select id="eventCreatePolicy" name="event_create_policy" class="form-control">
                <option value="members_allowed">Members can create events</option>
                <option value="mods_and_admins">Moderators / admins only</option>
                <option value="admins_only">Admins only</option>
              </select>
              <p class="form-text">Who can create events linked to this group.</p>
            </div>

            <div class="form-group">
              <label class="form-label" for="rssAddPolicy">RSS feed policy</label>
              <select id="rssAddPolicy" name="rss_add_policy" class="form-control">
                <option value="members_allowed">Members can add RSS feeds</option>
                <option value="mods_and_admins">Moderators / admins only</option>
                <option value="admins_only">Admins only</option>
              </select>
              <p class="form-text">Who can add external feeds that import into this group.</p>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" id="cancelBtn">Cancel</button>
            <button type="submit" class="btn btn-primary" id="submitBtn" ${this.submitting ? 'disabled' : ''}>
              ${this.submitting ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    `;
  }

  attachEventListeners() {
    const backBtn = this.querySelector('#backBtn');
    if (backBtn) {
      backBtn.addEventListener('click', () => this.navigateToGroups());
    }

    const cancelBtn = this.querySelector('#cancelBtn');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        if (confirm('Are you sure you want to cancel? Your changes will be lost.')) {
          this.navigateToGroups();
        }
      });
    }

    const form = this.querySelector('#groupCreateForm');
    if (form) {
      form.addEventListener('submit', (ev) => this.handleSubmit(ev));
    }
  }

  navigateToGroups() {
    if (window.router?.navigate) {
      window.router.navigate('/groups');
    } else {
      window.location.href = '/groups';
    }
  }

  async handleSubmit(ev) {
    ev.preventDefault();
    if (this.submitting || !window.API) {
      return;
    }

    const form = ev.target;
    const name = form.querySelector('[name="name"]')?.value?.trim();
    const description = form.querySelector('[name="description"]')?.value?.trim() || '';
    const invite_policy = form.querySelector('[name="invite_policy"]')?.value || 'members_allowed';
    const event_create_policy = form.querySelector('[name="event_create_policy"]')?.value || 'members_allowed';
    const rss_add_policy = form.querySelector('[name="rss_add_policy"]')?.value || 'members_allowed';

    if (!name) {
      this.error = 'Group name is required';
      this.render();
      this.attachEventListeners();
      return;
    }

    this.error = null;
    this.submitting = true;
    this.render();
    this.attachEventListeners();

    try {
      const group = await window.API.request('/groups', {
        method: 'POST',
        body: JSON.stringify({ name, description, invite_policy, event_create_policy, rss_add_policy })
      });

      const path = `/groups/${group.id}`;
      if (window.router?.navigate) {
        window.router.navigate(path);
      } else {
        window.location.href = path;
      }
    } catch (e) {
      logger.warn('GroupCreate submit failed', e);
      this.error = e.message || 'Failed to create group';
      this.submitting = false;
      this.render();
      this.attachEventListeners();
    }
  }

  escapeHtml(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('group-create', GroupCreate);
export default GroupCreate;
